import type { TranslationKeys } from './tr';

export const en: TranslationKeys = {
  // Header
  header: {
    title: 'VOLTRON',
    subtitle: 'AI Operation Control Center',
    connected: 'Connected',
    connecting: 'Connecting',
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected',
  },

  // Sidebar
  sidebar: {
    fileExplorer: 'File Explorer',
    protectionZones: 'Protection Zones',
    patternTester: 'Pattern Test',
    fileHistory: 'File History',
  },

  // ActionFeed
  actionFeed: {
    title: 'Live Action Feed',
    scrollToLatest: 'Scroll to latest',
    events: 'events',
    noEventsTitle: 'No events yet',
    noEventsDescription: 'AI action events will appear here in real-time as they are captured.',
  },

  // ActionFilter
  actionFilter: {
    filterByFilePath: 'Filter by file path...',
  },

  // ExecutionControls
  executionControls: {
    title: 'Execution Control',
    processed: 'Processed:',
    pending: 'Pending:',
    autoStopThreshold: 'Auto-stop threshold:',
    stopReason: 'Stop reason:',
    stop: 'STOP',
    continue: 'CONTINUE',
    reset: 'RESET',
    confirm: 'Confirm?',
    stateHistory: 'State History',
  },

  // RiskGauge
  riskGauge: {
    title: 'Risk Level',
    highestRisk: 'Highest Risk',
  },

  // RiskTimeline
  riskTimeline: {
    title: 'Risk Timeline',
    waitingForData: 'Waiting for sufficient data...',
    risk: 'Risk',
  },

  // StateHistory
  stateHistory: {
    title: 'State History',
    noTransitions: 'No transitions yet',
    total: 'total',
  },

  // ZoneManager
  zoneManager: {
    noZones: 'No protection zones defined.',
    addZone: 'Add zone',
    pathPattern: 'Path pattern (e.g., /etc/nginx/**)',
    reasonOptional: 'Reason (optional)',
    save: 'Save',
    cancel: 'Cancel',
    zoneCreated: 'Zone Created',
    zoneCreatedMessage: 'Protection zone "{path}" created.',
    createFailed: 'Create Failed',
    zoneDeleted: 'Zone Deleted',
    zoneDeletedMessage: 'Protection zone removed.',
    deleteFailed: 'Delete Failed',
    cannotDeleteSystem: 'Cannot delete system zone',
    deleteZone: 'Delete zone',
  },

  // FileHistory
  fileHistory: {
    operations: 'operations',
    notFound: 'No history found',
    failedToLoad: 'Failed to load history',
  },

  // ActivityChart
  activityChart: {
    title: 'Activity',
    waitingForData: 'Waiting for sufficient data...',
  },

  // RiskBreakdown
  riskBreakdown: {
    title: 'Risk Breakdown',
    waitingForData: 'Waiting for data...',
    total: 'Total',
  },

  // ProjectStats
  projectStats: {
    title: 'Statistics',
    totalEvents: 'Total Events',
    byRiskLevel: 'By Risk Level',
    byActionType: 'By Action Type',
  },

  // PatternTester
  patternTester: {
    title: 'Pattern Tester',
    globPattern: 'Glob Pattern',
    testPath: 'Test Path',
    fileTreeMatches: 'File Tree Matches:',
    more: 'more...',
    recursiveMatch: '= recursive match',
    singleLevelMatch: '= single level match',
    singleCharacter: '= single character',
  },

  // App
  app: {
    loadingVoltron: 'Loading Voltron...',
    connectionError: 'Connection Error',
    retry: 'Retry',
    project: 'Project',
    select: 'Select...',
    noProjects: 'No projects',
    failedToLoadProjects: 'Failed to load projects',
    actionFeed: 'Action Feed',
    github: 'GitHub',
    selectProjectFirst: 'Please select a project first',
    snapshots: 'Snapshots',
    behavior: 'Behavior',
    prompts: 'Prompts',
    smartSetup: 'Smart Setup',
  },

  // StatusBar (moved to end of file)

  // Notifications
  notifications: {
    executionStopped: 'Execution Stopped',
    executionResumed: 'Execution Resumed',
    executionReset: 'Execution Reset',
    riskAlert: 'Risk Alert',
    stoppedMessage: 'AI operation has been stopped by operator.',
    resumedMessage: 'AI operation has been resumed.',
    resetMessage: 'Execution state has been reset to IDLE.',
    stopFailed: 'Stop Failed',
    continueFailed: 'Continue Failed',
    resetFailed: 'Reset Failed',
    unknownError: 'Unknown error',
  },

  // EmptyState / Common
  common: {
    noFiles: 'No files',
    filesWillAppear: 'Files will appear as events are received.',
    loading: 'Loading...',
    ok: 'OK',
    close: 'Close',
    errorTitle: 'An error occurred',
    errorDescription: 'An unexpected error occurred in this section. You can try refreshing the page.',
    retry: 'Retry',
    technicalDetails: 'Technical Details',
  },

  // FileTree
  fileTree: {
    heatMap: 'Heat Map',
  },

  // DiffViewer
  diffViewer: {
    diffTruncated: 'Diff truncated - content is too large to display fully',
    unified: 'Unified View',
    split: 'Split View',
    oldFile: 'Old File',
    newFile: 'New File',
  },

  // DiffHeader
  diffHeader: {
    created: 'Created',
    modified: 'Modified',
    deleted: 'Deleted',
    renamed: 'Renamed',
    dirCreated: 'Dir Created',
    dirDeleted: 'Dir Deleted',
    dependency: 'Dependency',
    config: 'Config',
  },

  // GitHubReport
  github: {
    analyze: 'Analyze',
    analyzing: 'Analyzing...',
    dependencies: 'Dependencies',
    devDependencies: 'Dev Dependencies',
    breakingChanges: 'Breaking Changes',
    compliance: 'Compliance',
    enterRepoUrl: 'Enter a GitHub repo URL and start analysis',
    noBreakingChanges: 'No breaking changes detected',
    breakingChange: 'breaking change',
    noDependencyData: 'No dependency data',
    noComplianceData: 'No compliance data',
    complianceScore: 'Compliance Score',
    total: 'Total',
    outdated: 'Outdated',
    conflicts: 'Conflicts',
    versionConflicts: 'Version Conflicts',
    before: 'Before',
    after: 'After',
    pass: 'pass',
    fail: 'fail',
    warn: 'warn',
    searchAndAdapt: 'Search & Adapt with Claude',
    searchPlaceholder: 'e.g.: react login page tailwind',
    targetDir: 'Target directory',
    anyFramework: 'Any framework',
    searching: 'Searching...',
    searchAndAdaptBtn: 'Search & Adapt',
  },

  // Layout
  layout: {
    showSidebar: 'Show sidebar',
    hideSidebar: 'Hide sidebar',
    showPanel: 'Show panel',
    hidePanel: 'Hide panel',
  },

  // Operations
  operations: {
    fileCreate: 'Create',
    fileModify: 'Modify',
    fileDelete: 'Delete',
    fileRename: 'Rename',
    dirCreate: 'Dir+',
    dirDelete: 'Dir-',
    dependencyChange: 'Deps',
    configChange: 'Config',
  },

  // Risk Levels
  riskLevels: {
    none: 'NONE',
    low: 'LOW',
    medium: 'MED',
    high: 'HIGH',
    critical: 'CRIT',
  },

  // Snapshots
  snapshots: {
    title: 'Snapshot Browser',
    loadMore: 'Load more',
    noSnapshots: 'No snapshots yet',
    label: 'Label',
    setLabel: 'Set label',
    rollback: 'Rollback',
    rollbackConfirm: 'Are you sure you want to rollback to this snapshot?',
    rollbackSuccess: 'Rollback successful',
    rollbackMessage: 'Interceptor will restore to this git commit.',
    files: 'files',
    showFiles: 'Show files',
    hideFiles: 'Hide files',
    compare: 'Compare',
    selectForCompare: 'Select for compare',
    prune: 'Prune old snapshots',
    pruneConfirm: 'Are you sure you want to delete all snapshots except the last {keep}?',
    pruneSuccess: 'Prune successful',
    pruneMessage: '{deleted} snapshots deleted, {remaining} remaining.',
    critical: 'Critical',
    commit: 'Commit',
  },

  // Interceptor Status
  interceptor: {
    title: 'Agent Status',
    rate: 'Rate',
    state: 'State',
  },

  // Behavior Scoring
  behavior: {
    title: 'Behavior Score',
    calculate: 'Calculate',
    overallScore: 'Overall Score',
    riskScore: 'Risk Score',
    velocityScore: 'Velocity Score',
    complianceScore: 'Compliance Score',
    details: 'Details',
    totalActions: 'Total Actions',
    actionsPerMin: 'Actions/min',
    zoneViolations: 'Zone Violations',
    trend: 'Trend',
    noData: 'No behavior data yet',
    howItWorks: 'How it Works?',
    step1: 'Actions are automatically logged while agent is running',
    step2: 'Behavior score is calculated every 5 minutes',
    step3: 'Track past performance with trend graphs',
    dataWillAppear: 'Data will appear automatically when agent is active',
  },

  // Prompt Versioning
  prompts: {
    title: 'Prompt Versions',
    newVersion: 'New Version',
    namePlaceholder: 'Version name...',
    contentPlaceholder: 'Prompt content...',
    create: 'Create',
    cancel: 'Cancel',
    active: 'Active',
    activate: 'Activate',
    compareParent: 'Compare with parent',
    noVersions: 'No prompt versions yet',
    noChanges: 'No changes',
  },

  // Agent Orchestration
  agent: {
    spawnAgent: 'Spawn Agent',
    prompt: 'Prompt',
    promptPlaceholder: 'What should Claude do?',
    model: 'Model',
    targetDir: 'Target Directory',
    spawn: 'Spawn',
    pause: 'Pause',
    resume: 'Resume',
    kill: 'Kill',
    inject: 'Inject',
    tab: 'Agent',
    noLocation: 'Waiting for location...',
    extractingPlan: 'Extracting plan...',
    step: 'Step',
    context: 'Context',
    injectPlaceholder: 'Inject instruction to agent...',
    includeConstraints: 'Include simulator constraints',
    output: 'Output',
    search: 'Search...',
    export: 'Export',
    noOutput: 'No output yet',
    scrollToBottom: 'Scroll to bottom',
    bufferWarning: 'Output buffer is nearly full ({{count}}/{{max}}). Older entries are being trimmed.',
    gpsTracker: 'GPS Tracker',
    plan: 'Plan',
    promptInjector: 'Prompt Injection',
    agentOutput: 'Agent Output',
    searchAndAdapt: 'Search & Adapt',
    searchQuery: 'Search query...',
    framework: 'Framework',
    simulator: 'Simulator',
    refreshSimulator: 'Refresh Simulator',
    openSimulator: 'Open in New Tab',
    simulatorIdle: 'Start an agent to preview simulator',
    preview: 'Preview',
    previewMode: 'Preview',
    simulatorMode: 'Simulator',
    switchToSimulator: 'Switch to Simulator mode',
    switchToPreview: 'Switch to Preview mode',
    planExtractionFailed: 'Plan extraction failed',
    noPlanDetected: 'No plan detected in this session',
    planFromBreadcrumbs: 'Plan generated from file activity',
    agentCompleted: 'Agent completed',
    agentCrashed: 'Agent crashed',
    pauseFailed: 'Pause failed',
    resumeFailed: 'Resume failed',
    killFailed: 'Kill failed',
    sending: 'Sending...',
    sendingEdits: 'Sending changes to AI...',
    editsSent: 'Edits sent',
    aiApplying: 'AI is applying changes...',
    sendFailed: 'Send failed',
    couldNotSendEdits: 'Could not send changes',
    filesCreated: 'files created',
    filesProcessed: '{count} files processed',
    workOnFile: 'Work on this file: {path}',
    showFileList: 'Show files',
    hideFileList: 'Hide files',
    editorHint: 'Click: select | Drag: move | Double-click: text | Right-click: mark',
    toolSelect: 'Select',
    toolMove: 'Move',
    toolResize: 'Resize',
    toolColor: 'Color',
    toolFont: 'Font',
    toolText: 'Text',
    toolEffect: 'Effect',
    toolError: 'Mark Error',
    toolAdd: 'Add Here',
    toolNote: 'Note',
    colorText: 'Text',
    colorBg: 'Background',
    colorBorder: 'Border',
    customColor: 'Custom Color',
    fontSize: 'Size',
    shadow: 'Shadow',
    dragHint: 'Drag the selected element to move it',
    textHint: 'Click on element to edit its text',
    pendingEdits: 'edits pending',
    clearAll: 'Clear All',
    showEdits: 'Show Edits',
    hideEdits: 'Hide',
    saveAndSend: 'Send to AI',
    sendConfirm: {
      title: 'Send Changes',
      message: '{{count}} change(s) will be sent to AI. Do you want to continue?',
      cancel: 'Cancel',
      confirm: 'Send',
    },
    editList: 'Edits',
    removeEdit: 'Undo',
    errorNotePlaceholder: 'Describe the error: what is wrong here?',
    addNotePlaceholder: 'What should be added here?',
    annotationPlaceholder: 'Note...',
    fileMap: 'File Map',
    noNavigation: 'No file navigation yet',
    startAgent: 'Run an agent to start',
    mapWillActivate: 'Map will activate when agent starts file operations',
    gpsNavigation: 'GPS navigation map',
    clickToExpand: 'Click to expand',
    thinking: 'Thinking...',
    showOutput: 'Show Output',
    hideOutput: 'Hide Output',
    visits: 'visits',
    devServer: 'Dev Server',
    devServerStarting: 'Starting dev server...',
    devServerReady: 'Dev server ready',
    devServerError: 'Dev server failed',
    devServerPort: 'Port',
    devServerRestart: 'Restart',
    installing: 'Installing dependencies...',
    modelDescHaiku: 'Fast, economical',
    modelDescSonnet: 'Balanced, powerful',
    modelDescOpus: 'Maximum capability',
    detectedTools: 'Detected AI Tools',
    toolNotFound: 'Not found on this system',
    noSpawnableTools: 'No spawnable AI CLI tools detected. Install Claude Code, Aider, or Codex CLI.',
    quickUseTailwind: 'Use Tailwind',
    quickFollowPatterns: 'Follow Patterns',
    quickAddTypeScript: 'Add TypeScript',
    quickResponsive: 'Responsive',
    fixThisFile: 'Fix this file',
    testThis: 'Add tests for this',
    refactorThis: 'Refactor this component',
    improveStyle: 'Improve styling',
    continueCurrent: 'Continue with current task',
    switchTask: 'Switch to a new task',
    reviewChanges: 'Review changes so far',
    spawning: 'Spawning...',
    injecting: 'Injecting...',

    tempChangesWarning: 'Changes are temporary. Send to AI to make them permanent.',

    // Prompt History
    promptHistory: {
      title: 'Prompt History',
      historyTab: 'History',
      templatesTab: 'Templates',
      searchPlaceholder: 'Search history...',
      noMatchingPrompts: 'No matching prompts found',
      noHistoryYet: 'No prompt history yet',
      tryDifferentSearch: 'Try a different search term',
      promptsWillAppear: 'Prompts will appear here after you spawn an agent',
      copyToClipboard: 'Copy to clipboard',
      deleteFromHistory: 'Delete from history',
      of: 'of',
      prompts: 'prompts',
      clearAll: 'Clear All',
      clickToUse: 'Click to use',
      templateHint: 'Select a template to use as your prompt. You can edit it before spawning.',
    },

    // Quick Spawn Presets
    presets: {
      title: 'Quick Presets',
      landingPage: 'Landing Page',
      landingPageDesc: 'Modern landing page with hero, features, pricing, and contact',
      bugFix: 'Bug Fix',
      bugFixDesc: 'Analyze codebase, identify bugs, and fix them',
      fullApp: 'Full App',
      fullAppDesc: 'Build a complete web application from project requirements',
      apiBackend: 'API Backend',
      apiBackendDesc: 'REST API with CRUD endpoints, validation, and error handling',
      uiPolish: 'UI Polish',
      uiPolishDesc: 'Improve styling, animations, and responsive design',
      testSuite: 'Test Suite',
      testSuiteDesc: 'Comprehensive unit and integration tests',
      saveAsCurrent: 'Save current prompt as preset',
      presetName: 'Preset name...',
      save: 'Save',
    },

    // Rules
    rules: {
      title: 'Project Rules',
      placeholder: 'Write your project rules here...\n- Always use Tailwind\n- TypeScript strict mode\n- File names in kebab-case',
      active: 'Active',
      inactive: 'Inactive',
      toggle: 'Toggle Rules',
      saved: 'Saved',
      saving: 'Saving...',
      autoSaveHint: 'Auto-saves as you type',
      noRules: 'No rules defined',
      rulesWillBePrepended: 'Project rules will be prepended to prompt',
    },

    // Memory
    memory: {
      title: 'Project Memory',
      add: 'Add',
      edit: 'Edit',
      delete: 'Delete',
      pin: 'Pin',
      unpin: 'Unpin',
      pinned: 'Pinned',
      category: 'Category',
      search: 'Search...',
      noMemories: 'No memories yet',
      addNew: 'Add New Memory',
      categoryArchitecture: 'Architecture',
      categoryConventions: 'Conventions',
      categoryBugs: 'Known Bugs',
      categoryPatterns: 'Patterns',
      categoryGeneral: 'General',
      saveMemory: 'Save',
      cancelMemory: 'Cancel',
      pinnedWillBeIncluded: 'pinned memories will be included in context',
      titlePlaceholder: 'Title...',
      contentPlaceholder: 'Content...',
    },

    // Session History
    history: {
      title: 'Session History',
      noSessions: 'No sessions yet',
      rerun: 'Re-run',
      viewDetails: 'Details',
      copyPrompt: 'Copy Prompt',
      spawnWithSame: 'Spawn with Same Config',
      filesCount: 'files',
      tokensUsed: 'tokens',
      duration: 'Duration',
      ago: 'ago',
      completed: 'Completed',
      crashed: 'Crashed',
      empty: 'Empty',
    },

    // Context Menu
    contextMenu: {
      editText: 'Edit Text',
      copySelector: 'Copy Selector',
      changeColor: 'Change Color',
      colorText: 'Text Color',
      colorBg: 'Background',
      colorBorder: 'Border Color',
      fontSize: 'Font Size',
      addEffect: 'Add Effect',
      shadowNone: 'None',
      shadowLight: 'Light',
      shadowMedium: 'Medium',
      shadowStrong: 'Strong',
      shadowEmboss: 'Emboss',
      resize: 'Resize',
      markError: 'Mark Error',
      addHere: 'Add Here',
      addNote: 'Leave Note',
      addPrompt: 'Add Prompt',
      showPins: 'Show All Pins',
      hidePins: 'Hide Pins',
      addRefImage: 'Add Reference Image',
      clearAll: 'Clear All Edits',
      deletePin: 'Delete',
      editPin: 'Edit',
    },

    // Prompt Pins
    promptPin: {
      title: 'Prompt Pin',
      placeholder: 'Write your detailed instructions...',
      nearElement: 'Near element',
      coordinate: 'Coordinate',
      save: 'Save',
      cancel: 'Cancel',
      ctrlEnter: 'Ctrl+Enter to save',
      count: 'pins',
      editTitle: 'Edit Pin',
    },

    // Reference Image
    refImage: {
      title: 'Reference Image',
      opacity: 'Opacity',
      remove: 'Remove',
      show: 'Show',
      hide: 'Hide',
    },

    // Phase Execution
    phase: {
      title: 'Phase Tracker',
      phase: 'Phase',
      approve: 'Approve',
      reject: 'Reject',
      pending: 'Pending',
      running: 'Running',
      awaitingApproval: 'Awaiting Approval',
      approved: 'Approved',
      rejected: 'Rejected',
      completed: 'Completed',
      failed: 'Failed',
      styleChanges: 'Style Changes',
      contentAdditions: 'Content Additions',
      errorFixes: 'Error Fixes',
      acceptCriteria: 'Acceptance Criteria',
    },

    // GPS Controls
    // Layout
    layout: {
      gpsFocus: 'GPS Focus',
      editorFocus: 'Editor Focus',
      balanced: 'Balanced',
      showLeft: 'Show Left Panel',
      hideLeft: 'Hide Left Panel',
      showRight: 'Show Right Panel',
      hideRight: 'Hide Right Panel',
      doubleClickReset: 'Double-click to reset',
    },

    // Toast Notifications
    toast: {
      agentStarted: 'Agent started',
      agentCompleted: 'Agent completed successfully',
      agentCrashed: 'Agent crashed',
      agentPaused: 'Agent paused',
      agentResumed: 'Agent resumed',
      injectionApplied: 'Injection applied',
    },

    // Timeline
    timeline: {
      title: 'Activity Timeline',
      empty: 'No activity yet',
    },

    // Session Export
    sessionExport: {
      title: 'Session Export',
      downloadJson: 'Download JSON Report',
      copySummary: 'Copy Summary',
      copy: 'Copy',
      copied: 'Copied!',
      noSession: 'No active session',
    },

    // Window Manager
    windowManager: {
      panelMenu: 'Panel Menu',
      resetLayout: 'Reset Layout',
      presetIde: 'IDE Style',
      presetGps: 'GPS Focus',
      presetMonitor: 'Monitor',
      minimize: 'Minimize',
      maximize: 'Maximize',
      restore: 'Restore',
      close: 'Close',
      bringToFront: 'Bring to Front',
      panelVisualEditor: 'Visual Editor',
      panelGpsNavigator: 'GPS Navigator',
      panelAgentTracker: 'Agent Tracker',
      panelPhaseViewer: 'Phase Viewer',
      panelPromptInjector: 'Prompt Injector',
      panelActivityTimeline: 'Activity Timeline',
      panelAgentOutput: 'Agent Output',
      panelRulesEditor: 'Rules Editor',
      panelMemoryManager: 'Memory Manager',
      panelSessionHistory: 'Session History',
    },

    // Keyboard Shortcuts
    shortcuts: {
      title: 'Keyboard Shortcuts',
      gpsFullscreen: 'Ctrl+Shift+G: GPS Fullscreen',
      editorFullscreen: 'Ctrl+Shift+E: Editor Fullscreen',
      promptFocus: 'Ctrl+Shift+P: Focus Prompt',
      togglePause: 'Space: Pause/Resume',
      toggleBreakpoint: 'Ctrl+Shift+B: Toggle Breakpoint',
      toggleOutput: 'Ctrl+Shift+O: Toggle Output',
      escape: 'Escape: Exit Fullscreen',
    },

    // Editor Toolbar
    editorToolbar: {
      undo: 'Undo (Ctrl+Z)',
      redo: 'Redo (Ctrl+Y)',
      domTree: 'DOM Tree',
      diffView: 'Diff View',
    },

    // GPS auto-follow
    gps: {
      autoFollow: 'Auto-Follow',
      pauseAgent: 'Pause',
      stopAgent: 'Stop',
      sendPrompt: 'Send Prompt',
      switchFile: 'Switch File',
      viewOutput: 'View Current Output',
      fileInfo: 'File Info',
      duration: 'Duration',
      lastToolCalls: 'Last Tool Calls',
      doInFile: 'Do in this file',
      viewContent: 'View Content',
      viewHistory: 'View History',
      compact: 'Compact',
      expanded: 'Expanded',
      lineRange: 'Line',
      toolName: 'Tool',
      searchFiles: 'Search files...',
      heatmap: 'Heatmap',
      panMode: 'Pan Mode (Hand Tool)',
      minimap: 'Minimap',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      resetView: 'Reset View',
      stats: 'Statistics',
      timeline: 'Timeline',
      live: 'LIVE',
      replay: 'Replay',
      redirect: 'Redirect Agent Here',
      setBreakpoint: 'Set Breakpoint',
      setBreakpointDesc: 'Pause agent when it reaches this file',
      removeBreakpoint: 'Remove Breakpoint',
      removeBreakpointDesc: 'Remove breakpoint from this file',
      improveFileDesc: 'Ask agent to improve this file',
      copyPath: 'Copy Path',
      previewFile: 'Preview File',
      activity: 'Activity',
      noPreview: 'Preview not available',
      topFiles: 'Top Files',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit Fullscreen',
      visitCount: 'visits',
      avgPerFile: 'Avg/file',
      filesVisited: 'Files Visited',
      totalVisits: 'Total Visits',
      breakpointHit: 'Breakpoint Hit',
      injectionQueued: 'Injection Queued',
      conflictWarning: 'Conflict Warning',
      agentRedirected: 'Agent Redirected',
    },

    // Chat
    chat: {
      title: 'AI Chat',
      messages: 'messages',
      placeholder: 'Type a message...',
      empty: 'No messages yet. Agent responses will appear here.',
      attachFile: 'Attach File',
      send: 'Send',
    },

    // File Upload
    upload: {
      title: 'File Upload',
      dragDrop: 'Drag & drop files',
      maxSize: 'Max 10MB',
      uploading: 'Uploading...',
    },

    status: {
      idle: 'IDLE',
      spawning: 'SPAWNING',
      running: 'RUNNING',
      paused: 'PAUSED',
      injecting: 'INJECTING',
      stopping: 'STOPPING',
      crashed: 'CRASHED',
      completed: 'COMPLETED',
    },
    activity: {
      thinking: 'Thinking',
      reading: 'Reading',
      writing: 'Writing',
      searching: 'Searching',
      executing: 'Executing',
      waiting: 'Waiting',
      idle: 'Idle',
    },
  },

  // Login
  login: {
    title: 'VOLTRON',
    subtitle: 'AI Operation Control Center',
    username: 'Username',
    password: 'Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    signIn: 'Sign In',
    invalidCredentials: 'Username and password cannot be empty.',
    signOut: 'Sign Out',
    welcomeBack: 'Welcome Back',
    firstTimeSetup: 'First Time Setup',
    createAdminDesc: 'Create an admin account to access the system.',
    createAdmin: 'Create Admin Account',
    usernameMin: 'Username must be at least 3 characters.',
    passwordMin: 'Password must be at least 6 characters.',
    passwordMismatch: 'Passwords do not match.',
    passwordRequirements: 'Username min. 3 chars, password min. 6 chars.',
  },

  // Not Found
  notFound: {
    message: 'The page you are looking for does not exist.',
    backToDashboard: 'Back to Dashboard',
  },

  // Language
  language: {
    tr: 'TR',
    en: 'EN',
  },

  // Command Palette
  commandPalette: {
    placeholder: 'Type a command...',
    noResults: 'No matching commands found',
    panels: 'Panels',
    layout: 'Layout',
    agent: 'Agent',
    navigation: 'Navigation',
    show: 'Show',
    hide: 'Hide',
    maximize: 'Maximize',
    layoutIDE: 'IDE Layout',
    layoutGPS: 'GPS Focus Layout',
    layoutMonitor: 'Monitor Layout',
    layoutMinimal: 'Minimal Layout',
    layoutDevelopment: 'Development Layout',
    layoutReset: 'Reset Layout',
    recent: 'Recent',
    injectFix: 'Inject: Fix current file',
    injectExplain: 'Inject: Explain this code',
    showShortcuts: 'Show Keyboard Shortcuts',
    openSettings: 'Open Settings',
    switchLanguage: 'Switch Language',
    navigate: 'Navigate',
    execute: 'Execute',
    close: 'Close',
  },

  // Keyboard Shortcuts
  shortcuts: {
    title: 'Keyboard Shortcuts',
    global: 'Global',
    agent: 'Agent',
    panels: 'Panels',
    gps: 'GPS Navigation',
    commandPalette: 'Command Palette',
    showShortcuts: 'Show Shortcuts',
    emergencyStop: 'Emergency Stop',
    closeModal: 'Close Modal / Exit',
    pauseResume: 'Pause / Resume Agent',
    focusPrompt: 'Focus Prompt Input',
    toggleBreakpoint: 'Toggle Breakpoint',
    toggleOutput: 'Toggle Output Panel',
    sendPrompt: 'Send Prompt',
    gpsFullscreen: 'GPS Fullscreen',
    editorFullscreen: 'Editor Fullscreen',
    togglePanelMenu: 'Toggle Panel Menu',
    cyclePanels: 'Cycle Panels',
    searchFiles: 'Search Files',
    zoomInOut: 'Zoom In / Out',
    panAlways: 'Pan (always)',
    panMode: 'Pan mode drag',
    scroll: 'Scroll',
    middleClick: 'Middle Click',
    handTool: 'Hand Tool',
    footer: 'Press ? anytime to show this dialog',
  },

  // Welcome Tour
  tour: {
    title: 'Welcome to Voltron',
    subtitle: 'Let\'s take a quick tour to discover the key features.',
    skipTour: 'Skip Tour',
    previous: 'Previous',
    next: 'Next',
    finish: 'Finish',
    stepOf: '{current} / {total}',
    completedTitle: 'Tour Completed!',
    completedMessage: 'You\'re now ready to use Voltron. Happy building!',
    close: 'Get Started',
    step1Title: 'Try the Interactive Demo',
    step1Desc: 'No setup needed! Click "Try Demo" on the feed to watch the 14-rule risk engine classify file operations in real-time. Zero AI tokens required.',
    step2Title: 'Quick Prompt Bar',
    step2Desc: 'The prompt bar at the bottom lets you quickly spawn agents or inject instructions mid-session. Use Ctrl+Enter to submit. Quick action chips provide one-click common tasks.',
    step3Title: 'Agent Workspace',
    step3Desc: 'Your main workspace. Start, monitor, and manage AI agents. Prompt injection, phase tracking, GPS navigation, and output panels are all accessible from the Agent tab.',
    step4Title: 'Protection Zones',
    step4Desc: 'Mark critical files as DO_NOT_TOUCH to block dangerous modifications. The risk engine automatically flags operations that violate protection zones.',
    step5Title: 'GPS Navigation Map',
    step5Desc: 'Visualizes where the agent is working in the file system in real-time. Interactive map with heatmap, zoom, pan, and breakpoint features.',
    step6Title: 'Visual Editor & Reference Design',
    step6Desc: 'Edit directly on the UI with live preview. Upload a reference design screenshot and let AI generate similar components. Right-click context menu for 30+ options.',
    step7Title: 'Keyboard Shortcuts',
    step7Desc: 'Open the command palette with Ctrl+K, view all shortcuts with ?. Press Space to quickly pause/resume the agent.',
  },

  // Settings
  settings: {
    title: 'Settings',
    language: 'Language',
    defaultModel: 'Default AI Model',
    defaultLayout: 'Default Layout',
    notifications: 'Notifications',
    turkish: 'Turkish',
    modelFast: 'Fast',
    modelBalanced: 'Balanced',
    modelPowerful: 'Powerful',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    updatePassword: 'Update Password',
    passwordChanged: 'Password changed successfully.',
    pwFieldsRequired: 'Current and new password fields are required.',
  },

  // Smart Setup
  smartSetup: {
    title: 'Smart Setup',
    subtitle: 'AI-powered project configuration — autonomous discovery, automatic setup.',
    startDiscovery: 'Discover',
    applySelected: 'Apply Selected',
    analyzing: 'Analyzing project...',
    discovering: 'Searching GitHub...',
    evaluating: 'Evaluating...',
    ready: 'Ready — select repos and install',
    applying: 'Installing...',
    completed: 'Setup completed',
    failed: 'Setup failed',
    profileTitle: 'Project Profile',
    resultsTitle: 'Discovered Repos',
    relevance: 'Relevance',
    recommended: 'Recommended',
    noResults: 'No results found',
    existingSetup: 'Existing Setup',
    missingSetup: 'Missing',
    useGithub: 'Search GitHub for useful repos',
    skipGithubHint: 'Only project profile will be analyzed, no GitHub search will be performed.',
    profileOnly: 'Project profile analyzed. GitHub search was skipped.',
  },

  // Mobile UI
  mobile: {
    more: 'More',
    menu: 'Menu',
    showPanel: 'Show Panel',
    hidePanel: 'Hide Panel',
    feed: 'Feed',
    github: 'GitHub',
    agent: 'Agent',
    setup: 'Setup',
    snapshots: 'Snapshots',
    behavior: 'Behavior',
    prompts: 'Prompts',
    monitoring: 'Monitoring',
    theme: 'Theme',
    toggleSidebar: 'Toggle sidebar',
    bottomNav: 'Bottom navigation',
  },

  // Dashboard Mode
  dashboardMode: {
    essential: 'Essential',
    active: 'Active',
    allTools: 'All Tools',
  },

  // GPS Timeline
  gpsTimeline: {
    start: 'Start',
    pause: 'Pause',
    play: 'Play',
    skipToLive: 'Skip to live',
    live: 'LIVE',
  },

  // Getting Started
  gettingStarted: {
    title: 'Getting Started',
    dismiss: 'Dismiss',
    tryDemo: 'Try the Interactive Demo',
    tryDemoDesc: 'See how the risk engine classifies file operations in real-time.',
    startDemo: 'Start Demo',
    spawnAgent: 'Spawn Your First Agent',
    spawnAgentDesc: 'Give Claude a task and watch Voltron monitor every file change.',
    spawnAgentBtn: 'Spawn Agent',
    setupZones: 'Set Up Protection Zones',
    setupZonesDesc: 'Mark critical files as DO_NOT_TOUCH to block dangerous modifications.',
    customize: 'Customize Your Setup',
    customizeDesc: 'Set default model, language, and notification preferences.',
    openSettings: 'Open Settings',
  },

  // Live Preview
  livePreview: {
    loadFailed: 'Preview failed to load',
    unsupportedType: 'This file type cannot be previewed',
    useCodeView: 'Use code view for {ext} files',
    rendering: 'Rendering...',
    retry: 'Retry',
    refresh: 'Refresh',
  },

  // GPS File Preview
  gpsFilePreview: {
    changes: 'changes',
    files: 'files',
    clearFileTooltip: 'Clear changes for this file',
    clearAll: 'Clear All',
    sendAllToAI: 'Send All to AI',
    editTextTitle: 'Edit Text',
    newContentLabel: 'New content',
    newContentPlaceholder: 'Enter new text...',
    ctrlEnterHint: 'You can apply quickly with Ctrl+Enter',
    cancel: 'Cancel',
    apply: 'Apply',
    confirmCancel: 'Cancel',
    confirmOk: 'Confirm',
    changesSent: '{count} changes sent to AI',
    historyCleared: 'Change history cleared',
    askAIPrompt: 'I need help with this element:\\n{elementInfo}\\nSelector: {selector}\\nFile: {filePath}',
    styleApplied: 'Style applied',
    textUpdated: 'Text updated',
    elementDeleted: 'Element deleted',
    elementDuplicated: 'Element duplicated',
    visibilityToggled: 'Visibility toggled',
    visits: 'visits',
    live: 'Live',
    historyTooltip: 'Activity history',
    minimizeTooltip: 'Minimize',
    maximizeTooltip: 'Fullscreen',
    closeTooltip: 'Close (Esc)',
    activity: 'Activity',
    designModeHint: 'Right-click: Design mode',
    changesCount: '{count} changes',
    totalChanges: 'total {total} ({fileCount} files)',
    clearFileChanges: 'Clear changes for this file',
    sendAllToAITooltip: 'Send all file changes to AI',
    sendToAI: 'Send to AI',
    escCloseHint: 'Esc: Close',
    deleteElementTitle: 'Delete Element',
    deleteElementMessage: 'This element will be completely removed from the DOM. This action cannot be undone. Do you want to continue?',
  },

  // Status Bar
  statusBar: {
    events: 'events',
    noEvents: 'No events',
    emergencyStop: 'Ctrl+Shift+S: Emergency Stop',
    connected: 'WebSocket Connected',
    disconnected: 'WebSocket Disconnected',
    eventRate: 'Events per minute',
    uptime: 'Server Uptime',
    commandPalette: 'Commands',
  },
  // Design Context Menu
  designMenu: {
    // Categories
    content: 'Content',
    contentDesc: 'Text, font, alignment',
    appearance: 'Appearance',
    appearanceDesc: 'Color, opacity, border',
    layout: 'Layout',
    layoutDesc: 'Flex, grid, spacing',
    effects: 'Effects',
    effectsDesc: 'Shadow, blur, animation',
    structure: 'Structure',
    structureDesc: 'Copy, delete, reset',
    // Colors
    red: 'Red',
    orange: 'Orange',
    yellow: 'Yellow',
    green: 'Green',
    blue: 'Blue',
    purple: 'Purple',
    pink: 'Pink',
    neutral: 'Neutral',
    // Gradients
    sunset: 'Sunset',
    ocean: 'Ocean',
    forest: 'Forest',
    night: 'Night',
    gold: 'Gold',
    ice: 'Ice',
    fire: 'Fire',
    aurora: 'Aurora',
    carbon: 'Carbon',
    // Shadows
    none: 'None',
    light: 'Light',
    medium: 'Medium',
    strong: 'Strong',
    up: 'Up',
    blueGlow: 'Blue Glow',
    greenGlow: 'Green Glow',
    purpleGlow: 'Purple Glow',
    redGlow: 'Red Glow',
    // Actions
    editText: 'Edit Text',
    editTextDesc: 'Change the inner text of the selected element with premium editor.',
    boldLabel: 'Bold',
    boldDesc: 'Toggle font weight between bold/normal.',
    italicLabel: 'Italic',
    italicDesc: 'Toggle text between italic/normal.',
    underlineLabel: 'Underline',
    underlineDesc: 'Add or remove text underline.',
    fontIncrease: 'Font Size +2px',
    fontIncreaseDesc: 'Current: {size} → increase.',
    fontDecrease: 'Font Size -2px',
    fontDecreaseDesc: 'Current: {size} → decrease.',
    alignLeft: 'Align Left',
    alignCenter: 'Center',
    alignRight: 'Align Right',
    textColor: 'Text Color',
    textColorDesc: 'Current: {color}. Choose from wide palette.',
    bgColor: 'Background Color',
    bgColorDesc: 'Current: {color}. Choose color.',
    borderColor: 'Border Color',
    opacityUp: 'Opacity +10%',
    opacityUpDesc: 'Increase element visibility intensity.',
    opacityDown: 'Opacity -10%',
    opacityDownDesc: 'Make element more transparent. For layered design.',
    borderRadiusAdd: 'Round Corners +4px',
    borderRadiusAddDesc: 'Current: {radius}. Smoother corners.',
    borderRadiusRemove: 'Flatten Corners',
    borderAdd: 'Add Border',
    border2px: 'Thick Border',
    borderRemove: 'Remove Border',
    displayFlexDesc: 'Line up child elements side by side. display:flex.',
    displayGridDesc: 'Activate CSS Grid system. display:grid.',
    displayBlock: 'Block',
    displayBlockDesc: 'display:block — each element on new line.',
    displayInlineFlex: 'Inline Flex',
    displayNone: 'Hide',
    displayNoneDesc: 'Completely hide element with display:none.',
    centerFlex: 'Center with Flex',
    flexColumn: 'Flex Column',
    flexColumnDesc: 'flex-direction: column — stack elements vertically.',
    flexBetween: 'Flex Space-Between',
    flexBetweenDesc: 'Space elements evenly.',
    gapAdd: 'Gap +8px',
    gapAddDesc: 'Increase spacing between Flex/Grid items.',
    paddingAdd: 'Padding +8px',
    paddingAddDesc: 'Current: {padding}. Increase inner spacing.',
    paddingRemove: 'Padding -8px',
    marginAdd: 'Margin +8px',
    marginAddDesc: 'Current: {margin}. Increase outer spacing.',
    width100: 'Full Width',
    width100Desc: 'width: 100% — fill all available width.',
    positionRelative: 'Position: Relative',
    positionRelativeDesc: 'Allow shifting within flow.',
    positionAbsolute: 'Position: Absolute',
    positionAbsoluteDesc: 'Free positioning relative to parent element.',
    overflowHidden: 'Overflow Hidden',
    overflowHiddenDesc: 'Hide overflowing content.',
    shadowPresets: 'Shadow Presets',
    shadowPresetsDesc: '12 ready shadow effects. Glow, neon, premium...',
    animationPresets: 'Animation Presets',
    animationPresetsDesc: 'Fade, slide, bounce, pulse, spin...',
    blurAdd: 'Blur',
    blurAddDesc: 'filter: blur(4px) — glassmorphism for background.',
    blurRemove: 'Remove Blur',
    blurRemoveDesc: 'filter: none — restore clarity.',
    backdropBlur: 'Backdrop Blur',
    backdropBlurDesc: 'Blur background. Glass effect.',
    transitionSmooth: 'Transition: Smooth (0.3s)',
    transitionSmoothDesc: 'transition: all 0.3s ease — smooth transition.',
    transitionSpring: 'Transition: Spring (0.5s)',
    transitionSpringDesc: 'Spring effect transition animation.',
    scaleUp: 'Scale Up (110%)',
    scaleUpDesc: 'transform: scale(1.1) — enlarge element.',
    rotate5: 'Rotate 5 degrees',
    rotate5Desc: 'transform: rotate(5deg) — slight tilt.',
    transformReset: 'Reset Transform',
    transformResetDesc: 'transform: none — return to normal size and position.',
    gradientPresets: 'Gradient Presets',
    gradientPresetsDesc: '10 ready gradients. Sunset, ocean, neon...',
    glassmorphism: 'Glassmorphism',
    glassmorphismDesc: 'Glass effect: transparent bg + backdrop-blur + thin border.',
    glassDark: 'Glass Dark',
    glassDarkDesc: 'Dark glassmorphism: dark transparent glass effect.',
    neumorphism: 'Neumorphism',
    neumorphismDesc: 'Emboss effect: inner and outer shadow combination.',
    neonBorder: 'Neon Border',
    neonBorderDesc: 'Glowing neon border effect.',
    neonText: 'Neon Text',
    neonTextDesc: 'Show text with glowing neon effect.',
    frostedCard: 'Frosted Card',
    frostedCardDesc: 'Premium card style: glassmorphism + shadow + radius.',
    hoverLift: 'Hover Lift Effect',
    hoverLiftDesc: 'Lift on hover: translateY + shadow increase.',
    duplicate: 'Duplicate',
    duplicateDesc: 'Clone element and place right after it.',
    toggleVisibility: 'Toggle Visibility',
    toggleVisibilityDesc: 'Show/hide element (display none ↔ block).',
    deleteElement: 'Delete Element',
    deleteElementDesc: 'Completely remove from DOM. Confirmation required.',
    resetStyles: 'Reset All Styles',
    resetStylesDesc: 'Undo all CSS changes made to this element.',
    askAI: 'Ask AI',
    askAIDesc: 'Ask AI about this element or request changes.',
    undoTitle: 'Undo (Ctrl+Z)',
    undoLabel: 'Undo',
    // Color picker
    textColorLabel: 'Text Color',
    bgColorLabel: 'Background',
    borderColorLabel: 'Border Color',
    selectedColor: 'Selected color',
    applyColor: 'Apply Color',
    // Panels
    gradientPanelTitle: 'Gradient Presets',
    customGradient: 'Custom Gradient (CSS)',
    shadowPanelTitle: 'Shadow Presets',
    customShadow: 'Custom Shadow (CSS)',
    animationPanelTitle: 'Animation Presets',
    transitionLabel: 'Transition',
    fast: 'Fast',
  },

  // Demo
  demo: {
    phaseProjectSetup: 'Project Setup',
    phaseBuilding: 'Building Source',
    phaseConfigChanges: 'Config Changes',
    phaseCritical: '.env CRITICAL',
    phaseCascadeAttack: 'Cascade Attack',
    running: 'Demo Running',
    stop: 'Stop',
    starting: 'Starting...',
    tryDemo: 'Try Demo',
    inProgress: 'Demo in Progress',
    inProgressDesc: 'Watch the risk engine analyze file operations in real-time. Events stream through 5 escalating phases — from safe edits to a critical .env breach.',
    stopDemo: 'Stop Demo',
    seeInAction: 'See Voltron in Action',
    seeInActionDesc1: 'Watch the 14-rule risk engine classify file operations in real-time. No Claude CLI needed — synthetic events showcase the full protection pipeline.',
    seeInActionDesc2: '5 phases: safe setup, source building, config changes, critical .env access, and a cascade attack that triggers the circuit breaker.',
    startingDemo: 'Starting Demo...',
    tryInteractiveDemo: 'Try Interactive Demo',
    zeroTokens: 'Zero tokens, zero AI calls — pure synthetic events',
  },

  // Reference Design Upload
  referenceDesign: {
    title: 'Reference Design',
    uploading: 'Uploading...',
    uploadTitle: 'Upload Reference Design',
    uploadDesc: 'Drop a screenshot or click to browse — AI will generate similar UI',
    instructionPlaceholder: "Optional: specific instructions (e.g. 'use dark theme', 'add animations', 'make it responsive')...",
    generateBtn: 'Generate Similar UI',
    altText: 'Reference design',
  },

  // Editor panels
  editorPanels: {
    domTree: 'DOM Tree',
    noDOMData: 'No DOM data',
    visualDiff: 'Visual Diff ({count} edits)',
  },
} as const;
